import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  useSiteSettings,
  type AllSettings,
  type BrandingSettings,
  type ThemeSettings,
  type UISettings,
} from '@/contexts/SiteSettingsContext';
import {
  isBrandingEqual,
  isThemeEqual,
  isUiEqual,
} from '@/contexts/appearanceDraftEquality';
import {
  EMPTY_APPEARANCE_COMMIT_RESULT,
  normalizeAppearanceCommitError,
  type AppearanceCommitResult,
  type AppearanceCommitStatus,
  type AppearanceSection,
  type AppearanceSectionCommitResult,
} from '@/contexts/appearanceCommitTypes';

export type { AppearanceSection, AppearanceCommitResult, AppearanceCommitStatus, AppearanceSectionCommitResult };
export {
  EMPTY_APPEARANCE_COMMIT_RESULT,
  normalizeAppearanceCommitError,
  APPEARANCE_SECTION_ERROR_FALLBACK,
} from '@/contexts/appearanceCommitTypes';

/** Canonical editable Appearance draft — fields map 1:1 to SiteSettings types. */
export interface AppearanceDraft {
  theme: ThemeSettings;
  branding: BrandingSettings;
  ui: UISettings;
}

export const APPEARANCE_SECTION_ORDER: AppearanceSection[] = ['theme', 'branding', 'ui'];

export const APPEARANCE_SECTION_LABELS: Record<AppearanceSection, string> = {
  theme: 'Màu sắc',
  branding: 'Thương hiệu',
  ui: 'Giao diện',
};

export interface AppearanceDirtyState {
  theme: boolean;
  branding: boolean;
  ui: boolean;
}

interface AppearanceDraftContextValue {
  draft: AppearanceDraft;
  baseline: AppearanceDraft;
  server: AllSettings;
  isHydrated: boolean;
  isLoadingServer: boolean;
  patchTheme: (patch: Partial<ThemeSettings>) => void;
  patchBranding: (patch: Partial<BrandingSettings>) => void;
  patchUi: (patch: Partial<UISettings>) => void;
  /** Low-level section save — only for tests / coordinator internals. UI must use commitDirtySections. */
  saveTheme: () => Promise<void>;
  saveBranding: () => Promise<void>;
  /** Optional override applied to draft before PUT (avoids setState race on save). */
  saveUI: (override?: Partial<UISettings>) => Promise<void>;
  commitDirtySections: () => Promise<AppearanceCommitResult>;
  clearCommitResult: () => void;
  setBrandingUploadBusy: (busy: boolean) => void;
  discardTheme: () => void;
  discardBranding: () => void;
  discardUI: () => void;
  discardSection: (section: AppearanceSection) => void;
  discardAll: () => void;
  saving: Record<AppearanceSection, boolean>;
  isDirty: boolean;
  dirty: AppearanceDirtyState;
  dirtySections: AppearanceSection[];
  dirtyCount: number;
  isThemeDirty: boolean;
  isBrandingDirty: boolean;
  isUiDirty: boolean;
  isCommitting: boolean;
  isInteractionLocked: boolean;
  brandingUploadBusy: boolean;
  commitStatus: AppearanceCommitStatus;
  commitResult: AppearanceCommitResult;
}

const AppearanceDraftContext = createContext<AppearanceDraftContextValue | null>(null);

export function appearanceSectionEqual(
  a: ThemeSettings | BrandingSettings | UISettings,
  b: ThemeSettings | BrandingSettings | UISettings,
): boolean {
  if ('primary_color' in a && 'primary_color' in b) {
    return isThemeEqual(a, b);
  }
  if ('site_title' in a && 'site_title' in b) {
    return isBrandingEqual(a, b);
  }
  return isUiEqual(a as UISettings, b as UISettings);
}

export function toAppearanceDraft(settings: AllSettings): AppearanceDraft {
  return {
    theme: normalizeTheme(settings.theme),
    branding: normalizeBranding(settings.branding),
    ui: normalizeUi(settings.ui),
  };
}

export function normalizeTheme(theme: ThemeSettings): ThemeSettings {
  return {
    primary_color: theme.primary_color,
    accent_color: theme.accent_color,
    success_color: theme.success_color,
    warning_color: theme.warning_color,
    sidebar_bg_color: theme.sidebar_bg_color,
    is_dark_mode: !!theme.is_dark_mode,
  };
}

export function normalizeBranding(branding: BrandingSettings): BrandingSettings {
  return {
    site_title: branding.site_title ?? '',
    logo_url: branding.logo_url ?? null,
    favicon_url: branding.favicon_url ?? null,
    loading_image_url: branding.loading_image_url ?? null,
  };
}

export function normalizeUi(ui: UISettings): UISettings {
  return {
    enable_animations: ui.enable_animations !== false,
    enable_glass_effect: ui.enable_glass_effect !== false,
    loading_animation_type: ui.loading_animation_type ?? 'default',
    loader_indicator_duration_ms: ui.loader_indicator_duration_ms ?? 1000,
  };
}

function copyAppearanceDraft(draft: AppearanceDraft): AppearanceDraft {
  return {
    theme: normalizeTheme(draft.theme),
    branding: normalizeBranding(draft.branding),
    ui: normalizeUi(draft.ui),
  };
}

function mergeSectionFromServer<T extends AppearanceSection>(
  section: T,
  prev: AppearanceDraft,
  settings: AllSettings,
  isSectionDirty: boolean,
  isSectionSaving: boolean,
): AppearanceDraft[T] {
  if (isSectionDirty || isSectionSaving) {
    return prev[section];
  }
  if (section === 'theme') return normalizeTheme(settings.theme) as AppearanceDraft[T];
  if (section === 'branding') return normalizeBranding(settings.branding) as AppearanceDraft[T];
  return normalizeUi(settings.ui) as AppearanceDraft[T];
}

function computeDirtyFromDraft(draft: AppearanceDraft, baseline: AppearanceDraft): AppearanceDirtyState {
  return {
    theme: !isThemeEqual(draft.theme, baseline.theme),
    branding: !isBrandingEqual(draft.branding, baseline.branding),
    ui: !isUiEqual(draft.ui, baseline.ui),
  };
}

export function AppearanceDraftProvider({ children }: { children: ReactNode }) {
  const { settings, isLoading, updateTheme, updateBranding, updateUI } = useSiteSettings();
  const [draft, setDraft] = useState<AppearanceDraft>(() => toAppearanceDraft(settings));
  const [baseline, setBaseline] = useState<AppearanceDraft>(() => toAppearanceDraft(settings));
  const [isHydrated, setIsHydrated] = useState(false);
  const [saving, setSaving] = useState<Record<AppearanceSection, boolean>>({
    theme: false,
    branding: false,
    ui: false,
  });
  const [isCommitting, setIsCommitting] = useState(false);
  const [brandingUploadBusy, setBrandingUploadBusyState] = useState(false);
  const [commitResult, setCommitResult] = useState<AppearanceCommitResult>(EMPTY_APPEARANCE_COMMIT_RESULT);

  const savingRef = useRef(saving);
  savingRef.current = saving;

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;

  const isCommittingRef = useRef(false);
  const brandingUploadBusyRef = useRef(false);
  const commitInFlightRef = useRef<Promise<AppearanceCommitResult> | null>(null);
  const commitResultRef = useRef(commitResult);
  commitResultRef.current = commitResult;

  const isThemeDirty = !isThemeEqual(draft.theme, baseline.theme);
  const isBrandingDirty = !isBrandingEqual(draft.branding, baseline.branding);
  const isUiDirty = !isUiEqual(draft.ui, baseline.ui);

  const dirty: AppearanceDirtyState = useMemo(
    () => ({
      theme: isThemeDirty,
      branding: isBrandingDirty,
      ui: isUiDirty,
    }),
    [isThemeDirty, isBrandingDirty, isUiDirty],
  );

  const dirtySections = useMemo(
    () => APPEARANCE_SECTION_ORDER.filter((section) => dirty[section]),
    [dirty],
  );

  const dirtyCount = dirtySections.length;
  const isDirty = dirtyCount > 0;

  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const isInteractionLocked = isCommitting;
  const commitStatus = commitResult.status;

  // Hydrate draft + baseline from server when sections are clean and not saving.
  useEffect(() => {
    if (isLoading) return;

    const nextAppearance = (prev: AppearanceDraft): AppearanceDraft => {
      const next: AppearanceDraft = {
        theme: mergeSectionFromServer(
          'theme',
          prev,
          settings,
          dirtyRef.current.theme,
          savingRef.current.theme || isCommittingRef.current,
        ),
        branding: mergeSectionFromServer(
          'branding',
          prev,
          settings,
          dirtyRef.current.branding,
          savingRef.current.branding || isCommittingRef.current,
        ),
        ui: mergeSectionFromServer(
          'ui',
          prev,
          settings,
          dirtyRef.current.ui,
          savingRef.current.ui || isCommittingRef.current,
        ),
      };
      if (
        isThemeEqual(prev.theme, next.theme) &&
        isBrandingEqual(prev.branding, next.branding) &&
        isUiEqual(prev.ui, next.ui)
      ) {
        return prev;
      }
      return next;
    };

    setDraft(nextAppearance);
    setBaseline(nextAppearance);
    setIsHydrated(true);
  }, [isLoading, settings.theme, settings.branding, settings.ui]);

  const clearCommitResult = useCallback(() => {
    setCommitResult(EMPTY_APPEARANCE_COMMIT_RESULT);
  }, []);

  const invalidateCommitFeedbackForSection = useCallback((section: AppearanceSection) => {
    setCommitResult((prev) => {
      if (prev.status === 'idle' || prev.status === 'running') return prev;
      const stillFailed = prev.failed.filter((f) => f.section !== section);
      if (stillFailed.length === prev.failed.length && prev.failed.length === 0) {
        // success/partial with no failed list — clear on any patch after settle
        return EMPTY_APPEARANCE_COMMIT_RESULT;
      }
      if (stillFailed.length !== prev.failed.length) {
        return EMPTY_APPEARANCE_COMMIT_RESULT;
      }
      if (prev.status === 'success' || prev.status === 'partial' || prev.status === 'failed') {
        return EMPTY_APPEARANCE_COMMIT_RESULT;
      }
      return prev;
    });
  }, []);

  const patchTheme = useCallback(
    (patch: Partial<ThemeSettings>) => {
      if (isCommittingRef.current) return;
      invalidateCommitFeedbackForSection('theme');
      setDraft((prev) => ({ ...prev, theme: normalizeTheme({ ...prev.theme, ...patch }) }));
    },
    [invalidateCommitFeedbackForSection],
  );

  const patchBranding = useCallback(
    (patch: Partial<BrandingSettings>) => {
      if (isCommittingRef.current) return;
      invalidateCommitFeedbackForSection('branding');
      setDraft((prev) => ({
        ...prev,
        branding: normalizeBranding({ ...prev.branding, ...patch }),
      }));
    },
    [invalidateCommitFeedbackForSection],
  );

  const patchUi = useCallback(
    (patch: Partial<UISettings>) => {
      if (isCommittingRef.current) return;
      invalidateCommitFeedbackForSection('ui');
      setDraft((prev) => ({ ...prev, ui: normalizeUi({ ...prev.ui, ...patch }) }));
    },
    [invalidateCommitFeedbackForSection],
  );

  const setBrandingUploadBusy = useCallback((busy: boolean) => {
    brandingUploadBusyRef.current = busy;
    setBrandingUploadBusyState(busy);
  }, []);

  const reconcileSectionAfterSave = useCallback(
    (section: AppearanceSection, value: AppearanceDraft[AppearanceSection]) => {
      setBaseline((prev) => ({ ...prev, [section]: value }));
      setDraft((prev) => ({ ...prev, [section]: value }));
      baselineRef.current = {
        ...baselineRef.current,
        [section]: value,
      } as AppearanceDraft;
      draftRef.current = {
        ...draftRef.current,
        [section]: value,
      } as AppearanceDraft;
    },
    [],
  );

  const saveTheme = useCallback(async () => {
    if (isCommittingRef.current) return;
    setSaving((s) => ({ ...s, theme: true }));
    try {
      const payload = normalizeTheme(draftRef.current.theme);
      await updateTheme(payload);
      reconcileSectionAfterSave('theme', payload);
    } finally {
      setSaving((s) => ({ ...s, theme: false }));
    }
  }, [reconcileSectionAfterSave, updateTheme]);

  const saveBranding = useCallback(async () => {
    if (isCommittingRef.current) return;
    setSaving((s) => ({ ...s, branding: true }));
    try {
      const payload = normalizeBranding(draftRef.current.branding);
      await updateBranding(payload);
      reconcileSectionAfterSave('branding', payload);
    } finally {
      setSaving((s) => ({ ...s, branding: false }));
    }
  }, [reconcileSectionAfterSave, updateBranding]);

  const saveUI = useCallback(
    async (override?: Partial<UISettings>) => {
      if (isCommittingRef.current) return;
      setSaving((s) => ({ ...s, ui: true }));
      try {
        const payload = normalizeUi(
          override ? { ...draftRef.current.ui, ...override } : draftRef.current.ui,
        );
        if (override) {
          setDraft((prev) => ({ ...prev, ui: payload }));
          draftRef.current = { ...draftRef.current, ui: payload };
        }
        await updateUI(payload);
        reconcileSectionAfterSave('ui', payload);
      } finally {
        setSaving((s) => ({ ...s, ui: false }));
      }
    },
    [reconcileSectionAfterSave, updateUI],
  );

  const commitDirtySections = useCallback((): Promise<AppearanceCommitResult> => {
    if (commitInFlightRef.current) {
      return commitInFlightRef.current;
    }

    if (brandingUploadBusyRef.current) {
      const blocked: AppearanceCommitResult = {
        status: 'blocked',
        attempted: [],
        succeeded: [],
        failed: [],
        completedCount: 0,
        totalCount: 0,
      };
      setCommitResult(blocked);
      return Promise.resolve(blocked);
    }

    const snapshotDirty = APPEARANCE_SECTION_ORDER.filter(
      (section) => computeDirtyFromDraft(draftRef.current, baselineRef.current)[section],
    );

    if (snapshotDirty.length === 0) {
      const idle = { ...EMPTY_APPEARANCE_COMMIT_RESULT, status: 'idle' as const };
      setCommitResult(idle);
      return Promise.resolve(idle);
    }

    const payloads = {
      theme: normalizeTheme(draftRef.current.theme),
      branding: normalizeBranding(draftRef.current.branding),
      ui: normalizeUi(draftRef.current.ui),
    };

    let resolveCommit!: (result: AppearanceCommitResult) => void;
    const run = new Promise<AppearanceCommitResult>((resolve) => {
      resolveCommit = resolve;
    });
    // Set mutex synchronously before any await so double-click shares one chain.
    commitInFlightRef.current = run;

    void (async () => {
      isCommittingRef.current = true;
      setIsCommitting(true);
      setCommitResult({
        status: 'running',
        attempted: [...snapshotDirty],
        succeeded: [],
        failed: [],
        completedCount: 0,
        totalCount: snapshotDirty.length,
      });

      const succeeded: AppearanceSection[] = [];
      const failed: AppearanceSectionCommitResult[] = [];
      let completedCount = 0;

      try {
        for (const section of snapshotDirty) {
          setSaving((s) => ({ ...s, [section]: true }));
          try {
            if (section === 'theme') {
              await updateTheme(payloads.theme);
              reconcileSectionAfterSave('theme', payloads.theme);
            } else if (section === 'branding') {
              await updateBranding(payloads.branding);
              reconcileSectionAfterSave('branding', payloads.branding);
            } else {
              await updateUI(payloads.ui);
              reconcileSectionAfterSave('ui', payloads.ui);
            }
            succeeded.push(section);
          } catch (err) {
            const normalized = normalizeAppearanceCommitError(section, err);
            failed.push({
              section,
              status: 'error',
              message: normalized.message,
              errorCode: normalized.errorCode,
            });
          } finally {
            completedCount += 1;
            setSaving((s) => ({ ...s, [section]: false }));
            setCommitResult({
              status: 'running',
              attempted: [...snapshotDirty],
              succeeded: [...succeeded],
              failed: [...failed],
              completedCount,
              totalCount: snapshotDirty.length,
            });
          }
        }

        let status: AppearanceCommitStatus;
        if (failed.length === 0) {
          status = 'success';
        } else if (succeeded.length === 0) {
          status = 'failed';
        } else {
          status = 'partial';
        }

        const result: AppearanceCommitResult = {
          status,
          attempted: [...snapshotDirty],
          succeeded: [...succeeded],
          failed: [...failed],
          completedCount,
          totalCount: snapshotDirty.length,
        };
        setCommitResult(result);
        resolveCommit(result);
      } finally {
        isCommittingRef.current = false;
        setIsCommitting(false);
        commitInFlightRef.current = null;
      }
    })();

    return run;
  }, [reconcileSectionAfterSave, updateBranding, updateTheme, updateUI]);

  const discardSection = useCallback(
    (section: AppearanceSection) => {
      if (savingRef.current[section] || isCommittingRef.current) return;
      clearCommitResult();
      const baselineSection = baselineRef.current[section];
      setDraft((prev) => {
        if (section === 'theme') {
          return { ...prev, theme: normalizeTheme(baselineSection as ThemeSettings) };
        }
        if (section === 'branding') {
          return { ...prev, branding: normalizeBranding(baselineSection as BrandingSettings) };
        }
        return { ...prev, ui: normalizeUi(baselineSection as UISettings) };
      });
    },
    [clearCommitResult],
  );

  const discardTheme = useCallback(() => discardSection('theme'), [discardSection]);
  const discardBranding = useCallback(() => discardSection('branding'), [discardSection]);
  const discardUI = useCallback(() => discardSection('ui'), [discardSection]);

  const discardAll = useCallback(() => {
    if (
      savingRef.current.theme ||
      savingRef.current.branding ||
      savingRef.current.ui ||
      isCommittingRef.current ||
      brandingUploadBusyRef.current
    ) {
      return;
    }
    clearCommitResult();
    const next = copyAppearanceDraft(baselineRef.current);
    setDraft(next);
    draftRef.current = next;
  }, [clearCommitResult]);

  const value = useMemo<AppearanceDraftContextValue>(
    () => ({
      draft,
      baseline,
      server: settings,
      isHydrated,
      isLoadingServer: isLoading,
      patchTheme,
      patchBranding,
      patchUi,
      saveTheme,
      saveBranding,
      saveUI,
      commitDirtySections,
      clearCommitResult,
      setBrandingUploadBusy,
      discardTheme,
      discardBranding,
      discardUI,
      discardSection,
      discardAll,
      saving,
      isDirty,
      dirty,
      dirtySections,
      dirtyCount,
      isThemeDirty,
      isBrandingDirty,
      isUiDirty,
      isCommitting,
      isInteractionLocked,
      brandingUploadBusy,
      commitStatus,
      commitResult,
    }),
    [
      draft,
      baseline,
      settings,
      isHydrated,
      isLoading,
      patchTheme,
      patchBranding,
      patchUi,
      saveTheme,
      saveBranding,
      saveUI,
      commitDirtySections,
      clearCommitResult,
      setBrandingUploadBusy,
      discardTheme,
      discardBranding,
      discardUI,
      discardSection,
      discardAll,
      saving,
      isDirty,
      dirty,
      dirtySections,
      dirtyCount,
      isThemeDirty,
      isBrandingDirty,
      isUiDirty,
      isCommitting,
      isInteractionLocked,
      brandingUploadBusy,
      commitStatus,
      commitResult,
    ],
  );

  return (
    <AppearanceDraftContext.Provider value={value}>{children}</AppearanceDraftContext.Provider>
  );
}

export function useAppearanceDraft(): AppearanceDraftContextValue {
  const ctx = useContext(AppearanceDraftContext);
  if (!ctx) {
    throw new Error('useAppearanceDraft must be used within AppearanceDraftProvider');
  }
  return ctx;
}

export function useOptionalAppearanceDraft(): AppearanceDraftContextValue | null {
  return useContext(AppearanceDraftContext);
}
